import { useState, useCallback } from 'react';
import { useAppStore, selectActiveProfile } from '@/store';
import { useAlert } from '@ascend/alert';
import { AllergenGrid } from './AllergenGrid';
import { SeveritySelector } from './SeveritySelector';
import { Tooltip } from './Tooltip';
import styles from './ProfilePanel.module.css';

export function ProfilePanel() {
  const profiles = useAppStore((s) => s.profiles);
  const activeProfileId = useAppStore((s) => s.activeProfileId);
  const createProfile = useAppStore((s) => s.createProfile);
  const deleteProfile = useAppStore((s) => s.deleteProfile);
  const setActiveProfile = useAppStore((s) => s.setActiveProfile);
  const activeProfile = useAppStore(selectActiveProfile);
  const addCustomAllergen = useAppStore((s) => s.addCustomAllergen);
  const removeCustomAllergen = useAppStore((s) => s.removeCustomAllergen);
  const clearDemoData = useAppStore((s) => s.clearDemoData);
  const showLoadDemo = useAppStore((s) => s.showLoadDemo);
  const setShowLoadDemo = useAppStore((s) => s.setShowLoadDemo);
  const rawInput = useAppStore((s) => s.rawInput);
  const { confirm } = useAlert();

  const [newName, setNewName] = useState('');
  const [customName, setCustomName] = useState('');
  const [customSynonyms, setCustomSynonyms] = useState('');

  const handleCreate = useCallback(() => {
    if (newName.trim()) {
      createProfile(newName.trim());
      setNewName('');
    }
  }, [newName, createProfile]);

  const handleAddCustom = useCallback(() => {
    if (customName.trim() && activeProfile) {
      const synonyms = customSynonyms
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      addCustomAllergen({
        id: `custom-${Date.now()}`,
        name: customName.trim(),
        synonyms,
      });
      setCustomName('');
      setCustomSynonyms('');
    }
  }, [customName, customSynonyms, activeProfile, addCustomAllergen]);

  const handleDeleteProfile = useCallback(async (id: string, name: string) => {
    const ok = await confirm({
      title: 'Delete Profile?',
      message: `Profile "${name}" and its allergen settings will be permanently removed.`,
      danger: true,
      confirmLabel: 'Delete',
    });
    if (ok) deleteProfile(id);
  }, [confirm, deleteProfile]);

  return (
    <div className={styles.profilePanel}>
      <div>
        <div className={styles.sectionTitle}>Profiles</div>
        <div className={styles.profileList}>
          {profiles.map((p) => (
            <div
              key={p.id}
              className={`${styles.profileItem} ${p.id === activeProfileId ? styles.profileItemActive : ''}`}
            >
              <button
                className={styles.profileButton}
                onClick={() => setActiveProfile(p.id)}
                aria-label={`Switch to profile ${p.name}`}
              >
                {p.name}
              </button>
              <div className={styles.profileActions}>
                <button className={styles.smallButton} onClick={() => handleDeleteProfile(p.id, p.name)} aria-label={`Delete profile ${p.name}`}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className={styles.newProfileRow}>
          <input
            className={styles.newProfileInput}
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            placeholder="New profile name..."
            maxLength={50}
          />
          <button className={styles.newProfileButton} onClick={handleCreate} aria-label="Create new profile">Create</button>
        </div>
      </div>

      {activeProfile && (
        <>
          <div>
            <div className={styles.sectionTitle}>Allergens to Watch</div>
            <AllergenGrid />
          </div>

          <div>
            <div className={styles.sectionTitle}>Severity Levels <Tooltip content="Assign a personal severity level to each watched allergen. This affects how detections are highlighted in the Results panel." /></div>
            <SeveritySelector />
          </div>

          <div className={styles.customAllergenSection}>
            <div className={styles.sectionTitle}>Custom Allergens <Tooltip content="Add allergens not covered by the EU 14 or US 9 lists. Custom entries are matched by name during detection." /></div>
            <div className={styles.customAllergenInputs}>
              <input
                className={styles.newProfileInput}
                type="text"
                value={customName}
                onChange={(e) => setCustomName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustom(); }}
                placeholder="Add custom allergen..."
                maxLength={50}
              />
              <input
                className={styles.newProfileInput}
                type="text"
                value={customSynonyms}
                onChange={(e) => setCustomSynonyms(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddCustom(); }}
                placeholder="Synonyms (comma separated)..."
                maxLength={120}
              />
            </div>
            <button className={styles.newProfileButton} onClick={handleAddCustom} aria-label="Add custom allergen">Add</button>
            <div className={styles.customAllergenList}>
              {activeProfile.customAllergens.map((ca) => (
                <span key={ca.id} className={styles.customAllergenTag}>
                  {ca.name}
                  <button type="button" className={styles.removeTag} onClick={() => removeCustomAllergen(ca.id)} aria-label={`Remove ${ca.name}`}>×</button>
                </span>
              ))}
            </div>
          </div>
        </>
      )}

      <div className={styles.demoSection}>
        <div className={styles.sectionTitle}>Demo Data</div>
        <div className={styles.demoRow}>
          <label className={styles.demoToggle} htmlFor="pp-show-demo">
            <input
              id="pp-show-demo"
              type="checkbox"
              className={styles.demoToggleInput}
              checked={showLoadDemo}
              onChange={(e) => setShowLoadDemo(e.target.checked)}
            />
            Show Load Demo option
          </label>
        </div>
        {rawInput && (
          <button className={styles.clearButton} onClick={clearDemoData}>
            Clear demo data
          </button>
        )}
      </div>
    </div>
  );
}
